import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ACTIVITES } from "@/data/content";
import { submitContact } from "@/lib/api";

const EMPTY = { nom: "", prenom: "", commerce: "", telephone: "", email: "", activite: "", message: "" };

export const ContactForm = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const pack = params.get("pack");
    if (pack) {
      setForm((f) => ({ ...f, message: `Bonjour, je suis intéressé(e) par le ${pack} Pack. Pouvez-vous me recontacter ?` }));
    }
  }, [params]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.telephone || !form.email) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Demande envoyée ! Nous vous recontactons rapidement.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-success" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-10 text-center">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-50 text-green-600 mb-5">
          <CheckCircle2 size={34} />
        </div>
        <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">Merci pour votre message !</h3>
        <p className="text-slate-600 mb-6">Nous avons bien reçu votre demande et vous recontactons très vite.</p>
        <button
          data-testid="contact-new-message-button"
          onClick={() => setSent(false)}
          className="text-green-600 font-semibold hover:text-green-700"
        >
          Envoyer un autre message
        </button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="prenom">Prénom *</Label>
          <Input id="prenom" data-testid="contact-prenom" value={form.prenom} onChange={update("prenom")} placeholder="Marie" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="nom">Nom *</Label>
          <Input id="nom" data-testid="contact-nom" value={form.nom} onChange={update("nom")} placeholder="Dupont" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="commerce">Nom du commerce</Label>
        <Input id="commerce" data-testid="contact-commerce" value={form.commerce} onChange={update("commerce")} placeholder="Boulangerie du Centre" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="telephone">Téléphone *</Label>
          <Input id="telephone" data-testid="contact-telephone" value={form.telephone} onChange={update("telephone")} placeholder="06 12 34 56 78" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={update("email")} placeholder="vous@exemple.fr" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="activite">Type d'activité</Label>
        <Select value={form.activite} onValueChange={(v) => setForm({ ...form, activite: v })}>
          <SelectTrigger id="activite" data-testid="contact-activite">
            <SelectValue placeholder="Sélectionnez votre activité" />
          </SelectTrigger>
          <SelectContent>
            {ACTIVITES.map((a) => (
              <SelectItem key={a} value={a} data-testid={`activite-option-${a}`}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Votre message</Label>
        <Textarea id="message" data-testid="contact-message" value={form.message} onChange={update("message")} rows={4} placeholder="Parlez-nous de votre commerce et de vos besoins..." />
      </div>

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-xl px-6 py-3.5 font-semibold text-lg transition-colors shadow-sm"
      >
        {loading ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
        {loading ? "Envoi en cours..." : "Envoyer ma demande"}
      </button>
      <p className="text-xs text-slate-400 text-center">
        Vos données sont utilisées uniquement pour vous recontacter. Voir notre politique de confidentialité.
      </p>
    </form>
  );
};

export default ContactForm;
